import { getFirstModuleOfArray } from "../routes/manageRouting";
import { createLink } from "colbi_web_ui/lib/utils";
import { UserRoles } from "../enums/userRoles";
import { jwtDecode } from "jwt-decode";
import { urlRedirectLink } from "./routingUtils";
import { checkAccessByRole } from "./roleValidatorUtil";
import { SovosSuiteProductMenu, useSovosPlatformContext } from "mosaic-react-platform";
import { findProjectById } from "./mapPropertiesForMenu";
import { localStorageEnum } from "../components/GlobalLayout/util";
import { savePendoParamsFromUrl } from "./pendo";


/**
 * @description receives a navigation array and returns all nested links in the navigation
 * @param navigation
 * @returns returns nestedLinks array
 **/
export function buildNestedNavigationLinks(
    navigation,
    history,
    params,
    rootLabel,
    i18n,
    addRootLabel,
    concatenatedMenuLabels,
) {
    const nestedLinks = [];

    const nestedLinksItem = {
        id: "",
        content: null,
        label: null,
        key: "data",
        to: "",
        action: () => { },
        icon: null,
        disabled: true,
        toolTip: "Disabled",
    };

    const navArray = Array.isArray(navigation) ? navigation : (navigation?.navigation || []);

    for (let element of navArray) {
        const elementName = String(element.name).trim();
        const childNav = Array.isArray(element.navigation)
            ? element.navigation
            : (element.navigation?.navigation || []);
        if (element.navigation && childNav.length > 0) {
            buildNestedNavigationLinks(
                element.navigation,
                history,
                params,
                elementName,
                i18n,
                addRootLabel,
                rootLabel + "_" + elementName,
            ).forEach((nestedLink) => {
                nestedLinks.push(nestedLink);
            });
        } else {
            if (element.to) {
                let tmpName = elementName;

                if (addRootLabel) {
                    tmpName = `${rootLabel} ${elementName}`.replace(/\s?\(\d+\)/, "").trim();
                }
                const nestedItem = JSON.parse(JSON.stringify(nestedLinksItem));
                nestedItem.id = concatenatedMenuLabels + "_" + elementName;
                nestedItem.label = tmpName;
                // This is used to fork a new key to be used by the new menu
                nestedItem.content = tmpName.replaceAll(/\([\d\s]+\)/g, "");
                nestedItem.key = "data";
                nestedItem.action = () => { };
                nestedItem.icon = element.icon;

                const url = hydrateRoute(element.to, params);
                nestedItem.to = url;
                nestedItem.action = () => {
                    history.push(url, { selectedLink: concatenatedMenuLabels + "_" + elementName });
                };
                element?.viewMode && (nestedItem.viewMode = element.viewMode);
                delete nestedItem.disabled;
                delete nestedItem.toolTip;

                nestedLinks.push(nestedItem);
            }
        }
    }
    return nestedLinks;
}

/**
 * @description receives a navigation item, transform to a Static Link object and return it
 * @param item
 * @param history
 * @param translateIcons
 * @param params
 * @returns returns a static link object
 **/
export function transformNavigationItem2StaticLink(item, history, params, i18n, hasNewNavigation) {
    const itemName = i18n(String(item.name).replace(/,/g, "").trim());
    const outputItem = {
        id: `${item.name}_${item.level}`,
        label: itemName || "",
        key: "data",
        to: "",
        action: () => { },
        icon: item.icon,
        ...(item?.useInMenuTreeList ? { useInMenuTreeList: item?.useInMenuTreeList } : {}),
        ...(item?.viewMode ? { viewMode: item?.viewMode } : {}),
    };

    if (item.to) {
        const url = hydrateRoute(hasNewNavigation && item?.toNewNavigation ? item.toNewNavigation : item.to, params);
        outputItem.to = url;
        outputItem.action = () => {
            history.push(url, { selectedLink: `${item.name}_${item.level}` });
        };
    }

    return outputItem;
}

export const buildStaticLinks = (navigation, history, matchParams, i18n, hasNewNavigation) => {
    return (navigation || []).map((item) => {
        let outputItem = transformNavigationItem2StaticLink(item, history, matchParams, i18n, hasNewNavigation);
        if (item.navigation) {
            const nestedLinks = buildNestedNavigationLinks(
                item.navigation,
                history,
                matchParams,
                outputItem.id,
                i18n,
                false,
                outputItem.id,
            );

            if (nestedLinks.length > 0) {
                const viewMode = (nestedLinks || []).find((nestedLink) => nestedLink.viewMode)?.viewMode;
                if (viewMode) {
                    outputItem.viewMode = viewMode;
                }
                delete outputItem.action;
                outputItem.nestedLinks = nestedLinks;

                return outputItem;
            }
        }
        return outputItem;
    });
};

export function hydrateRoute(routePath, params) {
    const withoutLocale = (routePath || "").replace(/^\/:locale\(.+?\)\?/, "");
    const hydrated = withoutLocale.replace(/:[a-zA-Z]+(\(.+?\))?\??\*?/gi, (part) => {
        const paramName = part.slice(1).replace(/\(.*?\)\??\*?|\?|\*/, "");
        const defaultValue = part.match(/\?$/)
            ? ""
            : part.match(/^:/)
                ? part.match(/\((\w*)/)?.[1] || ""
                : part;
        return params[paramName] !== undefined ? params[paramName] : defaultValue;
    });
    return (
        params.locale && hydrated.match(/^\//) ? `/${params.locale}${hydrated}` : hydrated
    ).replace(/\/+/gi, "/");
}

export const setAccount = (account, locale, history, proxy, params) => {
    if (account && account.id) {
        if (account.typeEntity === "org") {
            history.push(`/${locale}/organizations/${account["id"]}`);
        } else {
            setProjectAccount(account, history, proxy, params);
        }
    }
};

export const setProjectAccount = (project, history, proxy, params) => {
    let link;
    if (project && project.fiscalYears && Object.keys(project.fiscalYears).length > 0) {
        const mostRecentFiscalYear = Math.max(...Object.keys(project.fiscalYears));
        const projectModules = project && project.modules;
        const findFirstModule = getFirstModuleOfArray(
            (projectModules || []).filter((r) => r.name !== "repository").map((elem) => elem.name),
        );
        const checkForOnlyRepo = (projectModules || []).some((elem) => elem.name === "archive");
        const moduleFound = (projectModules || []).find((elem) => elem.name === findFirstModule);
        //found an matched modules and not default (repository)
        let typeOfNavigation = "full";
        let moduleOrRouteToUse = "";
        const hasNewNavigation = (projectModules || []).some(({ name }) => name === "new_navigation")
        if (moduleFound && !checkForOnlyRepo) {
            if (!hasNewNavigation) {
                moduleOrRouteToUse = moduleFound.name;
            }
            if (moduleFound?.navigation_by_import_id !== 2) {
                const mostRecentFiscalYearData = project.fiscalYears[mostRecentFiscalYear]
                const viewModeKeys = !Array.isArray(mostRecentFiscalYearData) ? Object.keys(mostRecentFiscalYearData) : []

                if (viewModeKeys?.length) {
                    const filterFullImportId = viewModeKeys.reduce((acc, viewMode) => {
                        const importIdsArray = mostRecentFiscalYearData[viewMode]
                            .filter(({ id }) => id !== "full")
                            .map(({ id }) => id)

                        if (importIdsArray?.length) acc.push(importIdsArray)

                        return acc;
                    }, []).flat();

                    if (filterFullImportId?.length) {
                        typeOfNavigation = Math.max(...filterFullImportId);
                    }
                } else {
                    const importIdsArray = mostRecentFiscalYearData
                        .filter(({ id }) => id !== "full")
                        .map(({ id }) => id)

                    if (importIdsArray?.length) {
                        typeOfNavigation = Math.max(...importIdsArray);
                    }
                }
            }

            if (!hasNewNavigation && moduleFound.default_route?.length) {
                moduleOrRouteToUse = moduleFound.default_route;
            }
            link = createLink(
                null,
                proxy,
                {
                    ...project,
                    to: `/${project.id}/${mostRecentFiscalYear}/${typeOfNavigation}/${moduleOrRouteToUse || "overview"}`,
                },
                params,
            );
        }
    }

    if (!link) {
        //if no data imported navigate to repository
        link = createLink(
            null,
            proxy,
            { ...project, to: `/${project.id}/repository/redirect` },
            params,
        );
    }

    history.push(link);
};

export function parseJson(json) {
    try {
        return JSON.parse(json);
    } catch (e) {
        return json;
    }
}

export const metadataStringify = (colomunsConfiguration, availableConditionsFiels) => {
    return JSON.stringify({
        columnsConfiguration: colomunsConfiguration,
        availableConditionsFields: availableConditionsFiels,
    });
};

export const checkIfProjectHasFrModule = (currentArrayModules) => {
    if (currentArrayModules.includes("SAFTFRFILE")) {
        return true;
    }
    return false;
};

export const removeKeyRecursively = (metadata, keyToRemove) => {
    const parsedMetadata = parseJson(metadata);
    const tempMetadata = { ...parsedMetadata };
    for (let prop in tempMetadata) {
        if (typeof tempMetadata[prop] === "object") {
            removeKeyRecursively(tempMetadata[prop], keyToRemove);
        } else if (prop === keyToRemove) {
            delete tempMetadata[prop];
        }
    }
    return JSON.stringify(tempMetadata, undefined, 4);
};

export const findFileVersionValue = (metadata, keyToFind) => {
    const parsedMetadata = parseJson(metadata);
    for (let prop in parsedMetadata) {
        if (typeof parsedMetadata[prop] === "object") {
            let result = findFileVersionValue(parsedMetadata[prop], keyToFind);
            if (result !== undefined) {
                return result;
            }
        } else if (prop === keyToFind) {
            return parsedMetadata[prop];
        }
    }
};

export const decodeUserSession = (user) => {
    try {
        const decoded = jwtDecode(user.session);
        return decoded.user;
    } catch (e) {
        return null;
    }
};

export const redirectNotValid = (userInformation, userLocale, isSettings = false, auth) => {
    if (!userInformation) {
        if (auth && !auth.isLoading && !auth?.isAuthenticated) {
                savePendoParamsFromUrl();
                auth?.signinRedirect().catch((e) => {
                    console.error(sanitizeErrorForLogging(e)); // eslint-disable-line no-console
                });
            return true;
        } else {
            return urlRedirectLink(`/${userLocale}/login`, true);
        }
    }

    if (userInformation && userInformation.expired) {
        return urlRedirectLink(`/${userLocale}/reset_password`);
    }

    if (!isSettings) {
        if (userInformation && checkAccessByRole(userInformation, [UserRoles.BACKOFFICE])) {
            return urlRedirectLink(`/${userLocale}/settings/`);
        }
    }
};
//TODO: to refactor this function
export const BuildSovosNavProps = (
    initialLink = "",
    accounts,
    selectedAccount,
    { locale, history, proxy, params },
    userInformation,
    staticLinks,
    projectId = undefined,
) => {
    const { activeProduct } = useSovosPlatformContext();
    const _selectedAccount =
        projectId === void 0 ? selectedAccount : findProjectById(selectedAccount, projectId);
    return {
        initialSelectedLink: initialLink,
        product: activeProduct ?? { id: "SAF-T", name: "SAF-T" },
        setAccount: (account) => {
            const { viewMode, ...restParams } = params || {}
            account["id"] = account["id"].split("_").pop();
            setAccount(account, locale, history, proxy, restParams);
        },
        slots: {
            productSwitcher: SovosSuiteProductMenu,
        },
        accounts: accounts,
        selectedAccount: _selectedAccount,
        links: staticLinks || [],
        userInfo: userInformation,
    };
};

export const isNonProdEnviroment = (environment = "") => {
    return (
        ["dev", "development", "qa", "qa2", "uat"].find((keyword) =>
            environment.includes(keyword),
        ) || null
    );
};

export const isNumeric = (value = "") => {
    return !Number.isNaN(+`${value}`);
};

export const logoutFromSSO = async (auth) => {
    try {
        if (auth?.signoutRedirect && auth?.isAuthenticated && !auth?.isLoading) {
            await auth.clearStaleState();
            await auth.removeUser();
            window.localStorage.removeItem("login-mode");
            window.localStorage.removeItem("persist:session");
            localStorage.removeItem(localStorageEnum.SAFT_ID_TOKEN);
            await auth.signoutRedirect({
                id_token_hint: auth.user?.id_token,
            });
            return true;
        }
    } catch (e) {
        console.error("100 - Local storage", sanitizeErrorForLogging(e)); // eslint-disable-line no-console
        return false;
    }
};

export const columnAlignments = (metadataProps) => {
    switch (metadataProps?.type) {
        case "number":
        case "currency":
            return { ...metadataProps, align: "right" };
        default:
            return { ...metadataProps };
    }
};

export const transformEntityWords = (input, joinSpace = "") => {
    return input
        .replace(/csv_[a-z]{2}_/gi, "")
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(joinSpace);
};

export const replaceKey = (key) => {
    if (!key) return "";
    return key
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
        .trim();
};

/**
 * @description Applies theme with priority logic: Project > Organization > Default
 * @param {Object} organizationProjects - The organization projects data
 * @param {string} projectId - The current project ID
 * @param {Function} updateThemes - Function to update themes
 * @param {string} componentName - Name of the component for logging (optional)
 * @returns {Object} - Object containing themeToApply and themeSource
 */
export const applyThemeWithPriority = (organizationProjects, projectId, updateThemes) => {
    if (!organizationProjects || Object.keys(organizationProjects).length === 0) {
        return { themeToApply: null, themeSource: "" };
    }

    let themeToApply = null;
    let themeSource = "";

    // Priority 1: Check for mosaicThemeConfig at current project level
    if (
        organizationProjects.projects &&
        Array.isArray(organizationProjects.projects) &&
        projectId
    ) {
        const currentProject = organizationProjects.projects.find(
            (project) => project.id === projectId,
        );
        if (currentProject && Object.keys(currentProject?.mosaicThemeConfig || {}).length) {
            themeToApply = currentProject.mosaicThemeConfig;
            themeSource = `Current Project "${currentProject.name}"`;
        }
    }

    // Priority 2: If no project theme, check organization level
    if (!themeToApply && organizationProjects.mosaicThemeConfig) {
        themeToApply = organizationProjects.mosaicThemeConfig;
        themeSource = "Organization";
    }

    // Apply the theme
    updateThemes(themeToApply);

    return { themeToApply, themeSource };
};

/**
 * @description Sanitizes an error message for safe logging to prevent Log Injection (CWE-117).
 * Removes control characters, newlines, and other potentially dangerous characters.
 * @param {Error|string|unknown} error - The error to sanitize
 * @param {object|number} [options] - Options object or maxLength number (backwards-compatible)
 * @param {number} [options.maxLength=500] - Maximum length of the sanitized message
 * @param {boolean} [options.includeStack=false] - Whether to include the stack trace for Error objects
 * @returns {string} - Sanitized error message safe for logging
 */
export const sanitizeErrorForLogging = (error, options = {}) => {
    const { maxLength = 500, includeStack = false } =
        typeof options === "number" ? { maxLength: options } : options;

    let message = "";

    if (error instanceof Error) {
        message = error.message || "Unknown error";
        if (includeStack && error.stack) {
            message += " | Stack: " + error.stack;
        }
    } else if (typeof error === "string") {
        message = error;
    } else if (error && typeof error === "object") {
        try {
            message = JSON.stringify(error);
        } catch {
            message = "Error object could not be serialized";
        }
    } else {
        message = String(error ?? "Unknown error");
    }

    // Remove control characters (ASCII 0-31 except tab(9), newline(10), carriage return(13), and DEL(127))
    // Remove newlines and carriage returns to prevent log injection
    // Replace with space to maintain readability
    const sanitized = message
        .split("")
        .filter((char) => {
            const code = char.charCodeAt(0);
            // Keep tab(9), newline(10), carriage return(13), printable chars (32-126), and chars > 127
            return code === 9 || code === 10 || code === 13 || (code >= 32 && code !== 127);
        })
        .join("")
        .replace(/[\r\n]+/g, " ") // Replace newlines with space
        .replace(/\s+/g, " ") // Collapse multiple spaces
        .trim();

    // Truncate to maxLength to prevent log flooding
    if (sanitized.length > maxLength) {
        return sanitized.substring(0, maxLength) + "...[truncated]";
    }

    return sanitized;
};

export const availableMonthsArray = [
    { id: 1, name: "January" },
    { id: 2, name: "February" },
    { id: 3, name: "March" },
    { id: 4, name: "April" },
    { id: 5, name: "May" },
    { id: 6, name: "June" },
    { id: 7, name: "July" },
    { id: 8, name: "August" },
    { id: 9, name: "September" },
    { id: 10, name: "October" },
    { id: 11, name: "November" },
    { id: 12, name: "December" }
];

export const yearShiftArray = [
    { label: "-1", value: "-1", description: "fiscal_year_behind_calendar" },
    { label: "0", value: "0", description: "fiscal_year_aligns_calendar" },
    { label: "+1", value: "+1", description: "fiscal_year_ahead_calendar" }]

export const GLOBAL_HEADER_HEIGHT = 73;

export const hasModule = (modulesArray, module) => {
    return modulesArray?.includes(module);
}

export const resolveDualModulePrefix = (modulePrefix, viewMode) => {
    if (!modulePrefix || !viewMode) {
        return modulePrefix;
    }

    if (viewMode === "source" && modulePrefix.startsWith("saft_")) {
        return `csv_${modulePrefix.slice("saft_".length)}`;
    }

    if (viewMode === "legal" && modulePrefix.startsWith("csv_")) {
        return `saft_${modulePrefix.slice("csv_".length)}`;
    }

    return modulePrefix;
};
